import axios, { AxiosPromise } from 'axios'

export interface Prerequisites {
  dbUpdateRequired: boolean,
  enableWebMonitoring: boolean,
  fileEncryptionAesKeySize: number,
  fileEncryptionRsaKeySize: number,
  fipsEnabled: boolean,
  frLoginEnabled: boolean,
  hideSSOButton: boolean,
  innovationCenterNavLinkBaseUrl: string,
  theme: string,
  tipEnabled: boolean,
  version: string,
}
export const getPrerequisites = (): Promise<Prerequisites> => axios.get<Prerequisites>('/api/system/prerequisites').then(res => res.data)

export type DxAccounts = $TSFIXME
export const getDxStatus = (): Promise<DxAccounts> => axios.get<DxAccounts>('/advancedreporting/api/v4/accounts', {}).then(res => res.data)
export const getFRDxStatus = (dxStatusConfig: $TSFIXME): Promise<DxAccounts> => axios.get<DxAccounts>('/advancedreporting/api/v4/accounts', dxStatusConfig).then(res => res.data)

export interface RoleDashType { roleId: number, isDx: boolean }
export const getDxUserAccess = (): Promise<RoleDashType[]> => axios.get<RoleDashType[]>('/api/rest/roleDashTypes').then(res => res.data)

export interface FeatureFlagTypeMap {
  'analytics.ng-phrase-manager.suggestions.disable': boolean,
  'analytics.disable-sentiment': boolean,
  'analytics.allow-phrase-in-multiple-languages': boolean,
  'analytics.disable-transcription-only-export': boolean,
  'analytics.enable-interaction-summarization': boolean,
  'analytics.enable-advanced-sentiment': boolean,
  'analytics.enable-smart-tag': boolean,
  'analytics.enable-transcription-search': boolean,
  'arbys.genesysConfig': boolean,
  'cher.enableFloBulkContactExportMonitoringPage': boolean,
  'cher.fedramp-login-banner': string,
  'cher.innovationCenterNavLinkBaseUrl': string,
  'cher.show-nebula-alert-icon': boolean,
  'enablers.showNewFive9AcdConfigPage': boolean,
  'enablers.showNewTeamsPage': boolean,
  'flo-bce': boolean,
  'iam.forgerockUserSync': boolean,
  'iam.integration': boolean,
  'iam.allowMfaConfiguration': boolean,
  'qm.tenant.enableMultipleEvalForms': boolean,
  'qm.tenant.sendContactStatusNotification': boolean,
  'qm.tenant.useBranchingLogic': boolean,
  'qm.tenant.useSystemMetadataFields': boolean,
  'qm.tenant.enableBulkContactOperationsUpdateMetadata': boolean,
  'qm.tenant.sharedMediaDirectory': boolean,
  'qm-contact-goal-config-enabled': boolean,
  'qm-contact-goal-queue-enabled': boolean,
  'yvrfe.CDMUserAdmin': boolean,
  'yvrfe.data-library': boolean,
  'yvrfe.data-task-management': boolean,
  'yvrfe.settings': boolean,
  'yvrfe.sharing-groups-admin': boolean,
  'yvrfe.udm-admin': boolean,
  'platform.bulk-user-merge': boolean,
  'bi.yeti.embedding.enabled': boolean,
  'bi.yeti.embedding.clientapp.url': string,
  'bi.yeti.embedding.auth.url': string,
  'bi.yeti.embedding.backend.url': string,
  'bi.yeti.backend.url.dashboards': string,
  'cone.landingpage.useLandingPageService': boolean,
  'cone.landingpage.servicePath': string,
  'cone.landingpage.enableManagement': boolean,
  'cone.landingpage.managementModuleUrl': string,
  'cone.landingpage.managementCssUrl': string,
  'cone.maxtimeout': number,
  'cone.landingpage.timeout': number,
}
export type FeatureFlagName = keyof FeatureFlagTypeMap
interface UnloadedConfig { implementation: 'UNLOADED' }
interface PropertyFileConfig { implementation: 'PROPERTY_FILE', properties: Record<FeatureFlagName, string> }
interface LaunchDarklyConfig { implementation: 'LAUNCH_DARKLY', properties: { 'featureflags.launchdarkly.client-side-id': string } }
export type FeatureFlagConfig = UnloadedConfig | PropertyFileConfig | LaunchDarklyConfig
export const getLaunchDarklyConfig = (): Promise<FeatureFlagConfig> => axios.get<FeatureFlagConfig>('/api/rest/featureflag/config').then(res => res.data)

export interface AppProperties { convIntelligenceAppUrl: string, convIntelligenceWebUrl: string, convIntelligenceApiUrl: string }
export const getAppProperties = (): Promise<AppProperties> => axios.get<AppProperties>('/api/rest/app/properties').then(res => res.data)

export interface UserLicenses { licenses: string[] }
export const getUserLicenses = (userId: number): Promise<UserLicenses> => axios.get<UserLicenses>(`/api/rest/org/common/person/${userId}/license`).then(res => res.data)

interface DesktopProperty<Key extends string, Value> { name: Key, value: Value }
type TeleoptiAdminURLProperty = DesktopProperty<'teleoptiAdminURL', string>
type TeleoptiEnabledProperty = DesktopProperty<'teleoptiEnabled', 'true' | 'false'>
export type TeleoptiConfigProperties = [TeleoptiAdminURLProperty, TeleoptiEnabledProperty]
export const getTeleoptiConfig = (tenantId: number): Promise<TeleoptiConfigProperties> => axios.get<TeleoptiConfigProperties>(`/api/rest/desktop/properties?tenantId=${tenantId}&key=teleoptiAdminURL&key=teleoptiEnabled`).then(res => res.data)

export interface CustomTenantTheme {
  displayName: string,
  themeId: number,
  primaryColor: string,
  secondaryColor: string,
  navigationTextColor: string,
  defaultTheme: boolean,
  tabTitle: string,
  hasLogo: boolean,
  hasFavicon: boolean,
  oemTheme: string,
  helpUrl: string,
}
export const getTheme = (): AxiosPromise<CustomTenantTheme> => axios.get('/api/rest/theme')

export interface MaintenanceMessage {
  title: string,
  messageBefore: string,
  messageDuring: string,
  maintenanceStart: number,
  maintenanceEnd: number,
  notificationStart: number,
  messageCreation: number,
}
export const getMaintenanceMessage = (): AxiosPromise<MaintenanceMessage> => axios.get('/api/rest/maintenance/message')

export interface ApplicationPage { product: string, title: string, tokenizedUrl: string }
export interface LandingPageResponse { status: number, data: ApplicationPage | null }
export async function fetchLandingPage(serviceEndpoint: string, timeout = 1000): Promise<ApplicationPage | null> {
  const landingPage = await axios.get<LandingPageResponse>(serviceEndpoint + '/landingPage', { timeout })
  return landingPage.data.data
}
